import {
  FunctionComponent,
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';

import { EyeIcon, EyeSlashIcon } from '@heroicons/react/20/solid';
import CloseIcon from 'components/icons/close';
import ReactModal from 'react-modal';
import { CTAButton } from './click-to-purchase-cta';

import { Result } from 'lib/result';
import './auth-modal.css';

interface CreateAccountModalProps extends ReactModal.Props {
  user: {
    entityId: number
    firstName?: string;
    lastName?: string;
    isGuest: boolean;
    isNewGuest?: boolean;
    email: string;
    guestEmail?: string;
  }
  onRequestClose(): void;
  onSuccess(): void;
}

const CreateAccountModal: FunctionComponent<CreateAccountModalProps> = ({
  user,
  onRequestClose,
  onSuccess,
  isOpen,
  ...rest
}) => {
  const [email, setEmail] = useState(user?.guestEmail ?? user.email);
  const [firstName, setFirstName] = useState(user?.firstName);
  const [lastName, setLastName] = useState(user?.lastName);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  const passwordInputRef = useRef<HTMLInputElement>(null);


  useEffect(() => {
    ReactModal.setAppElement('html');
  }, []);

  useEffect(() => {
    if (passwordInputRef.current) {
      setIsFormValid(
        passwordInputRef.current.checkValidity()
      );
    }
  }, [password]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => passwordInputRef.current?.focus(), 0);
    }
  }, [isOpen]);


  const handleClose = useCallback(
    (event?: MouseEvent | KeyboardEvent) => {
      if (event) event.preventDefault();
      onRequestClose();
      document.body.focus();
    },
    [onRequestClose]
  );

  const handleClaimAccount = async ({
    customerId,
    email,
    firstName,
    lastName,
    password
  }: {
    customerId: number;
    email: string;
    firstName?: string;
    lastName?: string;
    password: string;
  }) => {
    try {
      const response = await fetch(`/api/claim-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          customerId,
          email,
          firstName,
          lastName,
          password
        }),
      });

      return await response.json();
    } catch (error: any) {
      return Result.fail<undefined>(error.message).toJSON();
    }
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      if (password.length) {
        if (password.length < 7) {
          return setError('Passwords must be at least 7 characters and contain both alphabetic and numeric characters.')
        }
      }

      const result = await handleClaimAccount({
        customerId: user.entityId,
        email,
        firstName,
        lastName,
        password
      });

      if (result.isSuccess) {
        setMessage('Account created successfully.');
        onSuccess();
      } else {
        setError(result.message);
      }

    } catch (error) {
      setError('Something went wrong')
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  return (
    <ReactModal
      {...rest}
      isOpen={isOpen}
      ariaHideApp={process.env.NODE_ENV !== 'test'}
      bodyOpenClassName="has-activeModal"
      className={{
        base: 'auth-modal modalContent',
        afterOpen: 'modalContent--afterOpen',
        beforeClose: 'modalContent--beforeClose'
      }}
      closeTimeoutMS={200}
      onRequestClose={handleClose}
      overlayClassName={{
        base: 'modalOverlay',
        afterOpen: 'auth-modal modalOverlay--afterOpen',
        beforeClose: 'modalOverlay--beforeClose'
      }}
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
      onAfterClose={() => {
        setError('');
        setMessage('');
      }}
    >
      <header className="modal-header">
        <span className="modal-header-text">
          Create An Account to Claim Your Easy Payment Plan
        </span>

        <a className="modal-close" data-test="modal-close-button" href="#" onClick={handleClose}>
          <CloseIcon />
        </a>
      </header>

      <div className="modal-body" data-test="modal-body">
        <p className="info">
          Create a 4Patriots account now to unlock the convenience, safety & security of automatic-payment processing.
        </p>

        <form className='create-account' onSubmit={handleSubmit}>
          <div className='column-group'>
            <div className="floating-label-group">
              <input
                type="firstName"
                value={firstName}
                placeholder=""
                required
                onChange={(e) => setFirstName(e.target.value)}
                name="firstName"
              />
              <label className="floating-label">First Name *</label>
            </div>
            <div className="floating-label-group">
              <input
                type="lastName"
                value={lastName}
                placeholder=""
                required
                onChange={(e) => setLastName(e.target.value)}
                name="lastName"
              />
              <label className="floating-label">Last Name *</label>
            </div>
          </div>
          <div className="floating-label-group">
            <input
              type="email"
              value={email}
              disabled
              name="email"
            />
            <label className="floating-label">Email Address *</label>
          </div>
          <div className="floating-label-group">
            <input
              type={passwordVisible ? 'text' : 'password'}
              value={password}
              required
              placeholder=" "
              pattern="^(?=.*[a-zA-Z])(?=.*\d)[\w\W]{7,}$"
              title="Password must be at least 7 characters long and contain both letters and numbers."
              onChange={(e) => setPassword(e.target.value)}
              ref={passwordInputRef}
              name="password"
            />
            <label className="floating-label">Password *</label>
            <a onClick={togglePasswordVisibility} className="form-toggle-password form-input-icon">
              {passwordVisible ? <EyeIcon /> : <EyeSlashIcon />}
            </a>
          </div>
          <div className="buttons">
            <CTAButton
              buttonColor="Primary"
              buttonSize="Medium"
              buttonEmbellish="None"
              buttonFontSize={18}
              buttonText="Create Account"
              loading={loading}
              disabled={!isFormValid}
              type="submit"
            />

            <button onClick={onRequestClose} className="btn-cancel" type="button">
              Cancel
            </button>
          </div>
        </form>
        {!!error && <p className="error-message">{error}</p>}
        {!!message && <p className="info-message">{message}</p>}
      </div>
    </ReactModal>
  );
};

export default CreateAccountModal;
